import { ReactElement } from "react"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCloudDownload, faCopy } from "@fortawesome/free-solid-svg-icons"
import { InlineLoading, Loading, Tab, Tabs } from "@wfp/ui"

import { useDownloadXlsFormSchemasByIdMutation } from "services/marketprices/xlsFormSchemas"
import { useDownloadReportExcelFileMutation, useGetReportByIdQuery } from "services/marketprices/reports"
import { useGetMSByIdQuery } from "services/marketprices/mappingSchemas"

import { formatDate } from "../../utils/helpers"
import { Report } from "../../interfaces/dataInterfaces"
import useCommon from "../../hooks/useCommon"

type Props = {
  data: Report | null
}

const InfoRow = ({ label, value }: { label: string; value: string | number | ReactElement }): ReactElement => (
  <>
    <div className="flex gap-2 text-gray-600 font-semibold">
      <span>{label}</span>
    </div>
    <div className=" text-gray-800">{value}</div>
  </>
)

const ReportInfo = ({ data }: Props): ReactElement => {
  const { showSnackBar } = useCommon()
  const { data: reportByIdData, isFetching: isLoadingInfo } = useGetReportByIdQuery(data?.reportId ?? 0, {
    skip: !data,
    refetchOnMountOrArgChange: true,
  })
  const { data: msByIdData } = useGetMSByIdQuery(reportByIdData?.mappingSchemaId ?? 0, {
    skip: !reportByIdData?.mappingSchemaId,
  })
  const [downloadExcelFile, { isLoading: isDownloading }] = useDownloadReportExcelFileMutation()
  const [downloadXlsForm, { isLoading: isDownloadingXls }] = useDownloadXlsFormSchemasByIdMutation()

  const handleCopyJSON = async (): Promise<void> => {
    try {
      const jsonString = JSON.stringify(JSON.parse(msByIdData?.mappingSchemaJSON ?? null), null, 2)
      await navigator.clipboard.writeText(jsonString)
      showSnackBar("JSON copied to clipboard successfully")
    } catch (error) {
      showSnackBar("Failed to copy JSON to clipboard", "error")
    }
  }

  return (
    <Tabs>
      <Tab label="Details" href="" role="tab" selected tabIndex={0}>
        {isLoadingInfo ? (
          <div className="min-h-[130px] d-flex align-center justify-center">
            <Loading withOverlay={false} small />
          </div>
        ) : (
          !!data && (
            <div className="p-1">
              <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 leading-6">
                <InfoRow label="Report ID" value={`${reportByIdData?.reportId}`} />
                <InfoRow label="Country" value={reportByIdData?.countryName || "N/A"} />
                <InfoRow
                  label="Created Date"
                  value={formatDate(reportByIdData?.reportCreateDate, "DD-MMMM-YYYY HH:mm") || "N/A"}
                />
                <InfoRow label="Source Type" value={msByIdData?.mappingSchemaType || "N/A"} />
                <InfoRow label="Mapping Schema" value={reportByIdData?.mappingSchemaName || "N/A"} />
                <InfoRow label="Status" value={reportByIdData?.reportStatusName || "N/A"} />
                <InfoRow
                  label="Comments"
                  value={
                    <span className="break-words text-justify">
                      {reportByIdData?.reportComment || <span className="text-gray-400 italic">No comments</span>}
                    </span>
                  }
                />

                {data?.sourceType?.toLowerCase() === "excel" && (
                  <InfoRow
                    label="Excel File"
                    value={
                      isDownloading ? (
                        <InlineLoading className="h-6" />
                      ) : (
                        <button
                          disabled={isDownloading}
                          type="button"
                          onClick={() =>
                            data?.reportId &&
                            downloadExcelFile({
                              reportId: data.reportId?.toString(),
                              countryName: data.countryName,
                              startDate: data.dateRange?.start,
                              endDate: data.dateRange?.end,
                            })
                          }
                          className="border-0 bg-transparent p-0 h-full flex items-center gap-2 text-[var(--interactive-01)] hover:text-blue-700 transition-colors cursor-pointer"
                          title={reportByIdData?.mappingSchemaName ?? "Download Excel file"}
                        >
                          <FontAwesomeIcon icon={faCloudDownload} />
                        </button>
                      )
                    }
                  />
                )}
              </div>
            </div>
          )
        )}
      </Tab>
      <Tab label="Schema" href="" role="tab" selected tabIndex={0}>
        <div className="p-1">
          <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 leading-6">
            <InfoRow
              label="Created Date"
              value={formatDate(msByIdData?.mappingSchemaCreateDate || "", "DD-MMMM-YYYY HH:mm") || "N/A"}
            />

            <InfoRow
              label="Last Updated"
              value={formatDate(msByIdData?.mappingSchemaUpdateDate || "", "DD-MMMM-YYYY HH:mm") || "N/A"}
            />

            {!!msByIdData?.xlsFormId && (
              <InfoRow
                label="XLS Form File"
                value={
                  isDownloadingXls ? (
                    <InlineLoading className="h-6" />
                  ) : (
                    <button
                      disabled={isDownloadingXls}
                      type="button"
                      onClick={() =>
                        msByIdData?.xlsFormId &&
                        downloadXlsForm({ fileId: msByIdData.xlsFormId, sourceName: msByIdData?.mappingSchemaName })
                      }
                      className="border-0 bg-transparent p-0 h-full flex items-center gap-2 text-[var(--interactive-01)] hover:text-blue-700 transition-colors cursor-pointer"
                      title={msByIdData?.mappingSchemaName ?? "Download XLS Form"}
                    >
                      <FontAwesomeIcon icon={faCloudDownload} />
                    </button>
                  )
                }
              />
            )}
          </div>

          <div className="mt-2 border-t border-gray-300">
            <div className="flex items-center justify-between mb-1">
              <p className="text-gray-600 font-semibold m-0">Schema Configuration</p>
              <FontAwesomeIcon
                icon={faCopy}
                onClick={handleCopyJSON}
                title="Copy JSON to clipboard"
                color="var(--interactive-01)"
                className="cursor-pointer"
              />
            </div>
            <div className="bg-gray-50 rounded-md border border-gray-200 p-3">
              <pre className="max-h-96 overflow-auto text-xs font-mono text-gray-800 leading-relaxed">
                {JSON.stringify(JSON.parse(msByIdData?.mappingSchemaJSON ?? null), null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </Tab>
    </Tabs>
  )
}

export default ReportInfo
